import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import {
    Alert,
    Button,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    TextField,
    Card,
    CardContent,
    Chip,
} from "@mui/material";
import {
    AttachMoney,
    Person,
    CalendarToday,
    AccountBalance,
    CheckCircle,
    ArrowBack,
    Warning,
} from "@mui/icons-material";
import { AdminProjectContext } from "../../contexts/AdminProjectContext";
import { AdminUserContext } from "../../contexts/AdminUserContext";
import { CommonContext } from "../../contexts/CommonContext";
import useAxios from "../../hooks/useAxios";
import { ADMIN_VIEWS, NEW_CASH_IN_URL } from "../../utils/constant";
import styles from "../../styles/scoped/admin/NewCashInForm.module.css";
import { logger } from '../../utils/logger';
import { formatAmount, formatNumber } from '../../utils/utilities';


interface TransactionWithBackendFields {
    _id: string;
    uuid: string;
    userUuid: string;
    projectUuid: string;
    amount: number;
    type: "CONTRIBUTION" | "CASH_OUT";
    status: "PENDING" | "COMPLETED" | "FAILED";
    month: string;
    expectedAmount: number;
    paidAmount: number;
    remainingAmount: number;
    createdAt: string;
}

interface NewCashInFormProps {
    selectedMenu: (view: number) => void;
}
type MonthOption = {
    value: string; // ex: "2025-09"
    label: string; // ex: "septembre 2025"
    expectedAmount: number;
    paidAmount: number;
    remainingAmount: number;
    transactions: TransactionWithBackendFields[];
};
export default function NewCashInForm({ selectedMenu }: NewCashInFormProps) {
    const { selectedProjectParticipant } = useContext(AdminUserContext);
    const { selectedProject, refreshProjects } = useContext(AdminProjectContext);
    const { setNeedToRefreshData, previousMenu } = useContext(CommonContext);
    const { getWithAxios, postWithAxios } = useAxios();
    const [monthOptions, setMonthOptions] = useState<MonthOption[]>([])
    const [selectedMonth, setSelectedMonth] = useState<string>("");
    const [amount, setAmount] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userTransactions, setUserTransactions] = useState<TransactionWithBackendFields[]>([]);
    const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
    const [globalError, setGlobalError] = useState("");
    const [globalInfo, setGlobalInfo] = useState("");
    const [formErrors, setFormErrors] = useState({ month: "", amount: "" });

    // Récupération des transactions
    useEffect(() => {
        const fetchUserTransactions = async () => {
            if (!selectedProject || !selectedProjectParticipant) return;

            setIsLoadingTransactions(true);
            setGlobalError("");

            try {
                const url = `/admin/projects/${selectedProject.uuid}/users/${selectedProjectParticipant.uuid}/transactions`;
                const res = await getWithAxios(url);

                // Le backend envoie directement les données groupées par mois dans res.transactions
                // Chaque élément contient : month, expectedAmount, paidAmount, remainingAmount, transactions[]
                const monthlyData = res.transactions || [];

                // Extraire toutes les transactions individuelles de tous les mois
                const allTransactions: TransactionWithBackendFields[] = monthlyData.flatMap((monthData: any) =>
                    (monthData.transactions || []).map((t: any) => ({
                        ...t,
                        month: t.month || monthData.month
                    }))
                );

                // Utiliser les données mensuelles agrégées du backend
                const months = buildMonthOptions(selectedProject, allTransactions, monthlyData);
                setMonthOptions(months);

                setSelectedMonth(getInitialSelectedMonth(months));
            } catch (error) {
                logger.error('Erreur lors de la récupération des transactions:', error);
                setGlobalError('Erreur lors du chargement des données de paiement');
            } finally {
                setIsLoadingTransactions(false);
            }
        };

        fetchUserTransactions();
    }, [selectedProject, selectedProjectParticipant]);
    const buildMonthOptions = (
        project: any,
        transactions: TransactionWithBackendFields[],
        backendMonthData?: any[]
    ): MonthOption[] => {
        if (!project) return [];

        const startDate = new Date(project.startDate);
        // Si endDate est null/undefined (nouveaux projets), utiliser une date calculée ou la date actuelle + 1 an
        let endDate: Date;
        if (project.endDate) {
            endDate = new Date(project.endDate);
        } else {
            // Fallback: calculer endDate basé sur les participants si disponible
            // ou utiliser startDate + 12 mois par défaut
            const totalShares = project.Users?.reduce((sum: number, u: any) => sum + (u.UserProject?.share || 0), 0) || 12;
            endDate = new Date(startDate);
            endDate.setMonth(endDate.getMonth() + totalShares);
        }

        const months: MonthOption[] = [];
        let current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);

        while (current <= endDate) {
            const value = `${current.getFullYear()}-${(current.getMonth() + 1).toString().padStart(2, "0")}`;

            // Vérifie si le backend a des infos pour ce mois
            const backendMonth = backendMonthData?.find(m => m.month === value);

            let txsForMonth = transactions.filter((t) => t.month === value);
            let paidAmount = txsForMonth.reduce((sum, t) => sum + (t.amount || 0), 0);
            let expectedAmount = (project.contribution || 0) * (selectedProjectParticipant?.totalShare || 1);

            if (backendMonth) {
                txsForMonth = backendMonth.transactions || txsForMonth;
                paidAmount = backendMonth.paidAmount ?? paidAmount;
                expectedAmount = backendMonth.expectedAmount ?? expectedAmount;
            }

            const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

            months.push({
                value,
                label: current.toLocaleDateString("fr-FR", { month: "long", year: "numeric" }),
                expectedAmount,
                paidAmount,
                remainingAmount,
                transactions: txsForMonth,
            });

            current.setMonth(current.getMonth() + 1);
        }

        return months;
    };

    const getInitialSelectedMonth = (monthOptions: MonthOption[]): string => {
        if (!monthOptions.length) return "";

        // Cherche le premier mois non soldé avec une contribution attendue
        const nextUnpaid = monthOptions.find(
            (m) => m.expectedAmount > 0 && m.remainingAmount > 0
        );

        if (nextUnpaid) return nextUnpaid.value;

        // Sinon → prendre le tout premier mois
        return monthOptions[0].value;
    };


    // Calculs basés sur le mois sélectionné
    const selectedTransactionMonth = useMemo(
        () => userTransactions.find((t) => t.month === selectedMonth),
        [userTransactions, selectedMonth]
    );

    useEffect(() => {
        if (!userTransactions.length) return;

        // Retrouver le mois actuellement sélectionné
        const currentIndex = userTransactions.findIndex(
            (t) => t.month === selectedMonth
        );

        if (currentIndex === -1) return;

        const currentTx = userTransactions[currentIndex];

        // Si le mois courant est soldé (remainingAmount = 0)
        if (currentTx.remainingAmount === 0) {
            // Chercher le prochain mois non soldé
            const next = userTransactions.find(
                (t, idx) => idx > currentIndex && t.remainingAmount > 0
            );

            if (next) {
                setSelectedMonth(next.month); // 👉 on bascule automatiquement
            }
        }
    }, [userTransactions, selectedMonth]);


    const currentMonthData = monthOptions.find(m => m.value === selectedMonth);
    const monthlyContribution = currentMonthData?.expectedAmount || 0;
    const totalAlreadyPaid = currentMonthData?.paidAmount || 0;
    const remainingAmountForMonth = currentMonthData?.remainingAmount || 0;

    // Détection du dépassement pour feedback visuel
    const amountNum = parseFloat(amount);
    const exceedsRemainingAmount = !isNaN(amountNum) && amountNum > remainingAmountForMonth;

    const validateForm = () => {
        const errors = { month: "", amount: "" };

        const numAmount = parseFloat(amount);
        if (!amount || isNaN(numAmount) || numAmount <= 0) {
            errors.amount = "Veuillez saisir un montant valide";
        } else if (numAmount > remainingAmountForMonth) {
            errors.amount = `Le montant ne peut pas dépasser ${formatAmount(remainingAmountForMonth, selectedProject?.currency)}`;
        }

        if (!selectedMonth) errors.month = "Veuillez sélectionner un mois";

        setFormErrors(errors);
        return !errors.month && !errors.amount;
    };

    const canSubmit = !!selectedMonth && !!amount && !exceedsRemainingAmount && remainingAmountForMonth > 0 && !isSubmitting;

    const handleSubmit = async () => {
        if (!validateForm() || !selectedProject || !selectedProjectParticipant) return;

        setIsSubmitting(true);
        setGlobalError("");

        try {
            const payload = {
                amount: parseFloat(amount),
                month: selectedMonth,
                type: "CONTRIBUTION",
                description: `Contribution ${selectedMonth}`,
            };

            const response = await postWithAxios(
                NEW_CASH_IN_URL(selectedProject.uuid!, selectedProjectParticipant.uuid),
                payload
            );

            if (response.success && response.transaction) {
                // Recharger les transactions depuis le backend pour avoir les données à jour
                const url = `/admin/projects/${selectedProject.uuid}/users/${selectedProjectParticipant.uuid}/transactions`;
                const refreshedData = await getWithAxios(url);

                // Le backend envoie directement les données groupées par mois
                const refreshedMonthlyData = refreshedData.transactions || [];

                // Extraire toutes les transactions individuelles
                const refreshedAllTransactions: TransactionWithBackendFields[] = refreshedMonthlyData.flatMap((monthData: any) =>
                    (monthData.transactions || []).map((t: any) => ({
                        ...t,
                        month: t.month || monthData.month
                    }))
                );

                setUserTransactions(refreshedAllTransactions);
                const updatedMonthOptions = buildMonthOptions(selectedProject, refreshedAllTransactions, refreshedMonthlyData);
                setMonthOptions(updatedMonthOptions);

                // Auto-select prochain mois si celui-ci est soldé
                const updatedMonthOption = updatedMonthOptions.find((m) => m.value === response.transaction.month);

                if (updatedMonthOption && updatedMonthOption.remainingAmount === 0) {
                    const nextUnpaid = updatedMonthOptions.find(
                        (m) => m.value > response.transaction.month && m.remainingAmount > 0
                    );
                    if (nextUnpaid) {
                        setSelectedMonth(nextUnpaid.value);
                    }
                }

                // Réinitialiser le montant saisi
                setAmount("");
                setGlobalInfo("Encaissement enregistré avec succès");
                setNeedToRefreshData(true);

                // Rafraîchir les participants pour mettre à jour le statut upToDate
                await refreshProjects();
            }
            else {
                setGlobalError(response.message || "Erreur lors de l'encaissement");
            }
        } catch (error: any) {
            logger.error('Erreur encaissement complète:', error);
            logger.error('Type d\'erreur:', typeof error);
            logger.error('error.message:', error?.message);
            logger.error('error.response:', error?.response);
            logger.error('error.response?.data:', error?.response?.data);

            // Extraire le message d'erreur détaillé du backend
            let errorMessage = "Erreur lors de l'encaissement";

            if (error instanceof Error) {
                errorMessage = error.message;
                logger.debug('Message extrait de Error:', errorMessage);
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
                logger.debug('Message extrait de response.data.message:', errorMessage);
            } else if (error?.response?.data?.error?.message) {
                errorMessage = error.response.data.error.message;
                logger.debug('Message extrait de response.data.error.message:', errorMessage);
            } else if (typeof error === 'string') {
                errorMessage = error;
                logger.debug('Message extrait de string:', errorMessage);
            }

            logger.debug('Message final setGlobalError:', errorMessage);
            setGlobalError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };





    const handleBackClick = () => selectedMenu(previousMenu || ADMIN_VIEWS.DASHBOARD_VIEW);

    return (
        <div className={styles.cashInContainer}>
            <div className={styles.sectionHeader}>
                <div className={styles.headerLeft}>
                    <div className={styles.sectionIcon}>
                        <AttachMoney />
                    </div>
                    <h1 className={styles.sectionTitle}>Nouvel Encaissement</h1>
                </div>
                <button className={styles.backButton} onClick={handleBackClick}>
                    <ArrowBack /> Retour
                </button>
            </div>

            <Snackbar open={!!globalInfo} autoHideDuration={6000} onClose={() => setGlobalInfo("")} message={globalInfo} />

            <Card className={styles.infoCard}>
                <CardContent className={styles.infoCardContent}>
                    <div className={styles.participantInfo}>
                        <div className={styles.participantAvatar}>
                            {selectedProjectParticipant?.firstname?.[0]}
                            {selectedProjectParticipant?.lastname?.[0]}
                        </div>
                        <div className={styles.participantDetails}>
                            <h3 className={styles.participantName}>{selectedProjectParticipant?.firstname} {selectedProjectParticipant?.lastname}</h3>
                            <div className={styles.participantMeta}>
                                <Chip className={styles.metaChip} icon={<Person />} label={`Membre depuis ${selectedProjectParticipant?.createdAt ? new Date(selectedProjectParticipant.createdAt).toLocaleDateString() : 'N/A'}`} variant="outlined" />
                                <Chip className={styles.metaChip} icon={<AccountBalance />} label={`${selectedProjectParticipant?.totalShare || 0} parts`} variant="outlined" />
                            </div>
                        </div>
                    </div>

                    <div className={styles.paymentSummary}>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Contribution mensuelle</p>
                            <h4 className={styles.summaryValue}>{formatAmount(monthlyContribution, selectedProject?.currency)}</h4>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Total déjà payé</p>
                            <h4 className={styles.summaryValue}>{formatAmount(totalAlreadyPaid, selectedProject?.currency)}</h4>
                        </div>
                        <div className={styles.summaryItem}>
                            <p className={styles.summaryLabel}>Reste à payer (mois sélectionné)</p>
                            <h4 className={styles.summaryValue}>{formatAmount(remainingAmountForMonth, selectedProject?.currency)}</h4>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className={styles.formCard}>
                <CardContent className={styles.formCardContent}>
                    <h3 className={styles.formTitle}><CalendarToday /> Détails de l'encaissement</h3>

                    {isLoadingTransactions ? (
                        <Alert severity="info">Chargement des données de paiement...</Alert>
                    ) : (
                        <>
                            {globalError && <Alert severity="error" sx={{ mb: 2 }}>{globalError}</Alert>}

                            <InputLabel>Mois de contribution</InputLabel>
                            <Select
                                fullWidth
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                                displayEmpty
                                sx={{ fontSize: '0.95rem' }}
                            >
                                {monthOptions.map((m) => {
                                    const isSolde = m.paidAmount >= m.expectedAmount;
                                    return (
                                        <MenuItem key={m.value} value={m.value}>
                                            <span style={{ flex: 1 }}>{m.label}</span>
                                            <Chip
                                                label={`${formatNumber(m.paidAmount)} / ${formatNumber(m.expectedAmount)} ${selectedProject?.currency}`}
                                                size="small"
                                                variant={isSolde ? "filled" : "outlined"}
                                                color={isSolde ? "success" : "warning"}
                                            />
                                        </MenuItem>
                                    );
                                })}
                            </Select>


                            <InputLabel>Montant ({selectedProject?.currency})</InputLabel>
                            <TextField
                                fullWidth
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                error={exceedsRemainingAmount}
                                inputProps={{ min: 0, step: 0.01 }}
                                helperText={
                                    exceedsRemainingAmount
                                        ? `⚠️ Le montant dépasse le reste à payer (${formatAmount(remainingAmountForMonth, selectedProject?.currency)})`
                                        : `Maximum: ${formatAmount(remainingAmountForMonth, selectedProject?.currency)}`
                                }
                                sx={{
                                    '& .MuiFormHelperText-root': {
                                        color: exceedsRemainingAmount ? 'error.main' : 'text.secondary',
                                        fontWeight: exceedsRemainingAmount ? 600 : 400
                                    }
                                }}
                            />

                            {remainingAmountForMonth <= 0 && <Alert severity="warning"> Ce mois est déjà entièrement payé.</Alert>}

                            <Button
                                className={styles.submitButton}
                                fullWidth onClick={handleSubmit} disabled={!canSubmit} startIcon={<CheckCircle />}>
                                {isSubmitting ? "Traitement en cours..." : "Confirmer l'encaissement"}
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
